import PropTypes from 'prop-types';
import React, { useState } from 'react';
import {
  Col,
  ControlLabel,
  ButtonGroup,
  Button,
  Form,
  FormControl,
  FormGroup,
  Checkbox,
  Row,
} from 'react-bootstrap';
import { Tabs, Tab } from 'react-bootstrap/lib';
import { clone, cloneDeep, get, some, set } from 'lodash';
import {
  Condition,
  FinanceType,
  Origin,
  CLIENT_TYPE,
} from '@upstart/harmony/lib/constants';
import './DealerEditor.scss';

function OwnershipOptionsConfig(props) {
  const defaultOwnershipRule = {
    enabled: true,
    maxMiles: null,
    maxAge: null,
    maxPrice: null,
  };

  const { dealer, actions, fetching, onBack } = props;
  const defaultOwnershipOptions = {
    disabledDefaultPayment: false,
    [Condition.NEW]: {
      [CLIENT_TYPE.ONLINE]: {
        [FinanceType.PURCHASE]: clone(defaultOwnershipRule),
        [FinanceType.LEASE]: clone(defaultOwnershipRule),
        [FinanceType.CASH]: clone(defaultOwnershipRule),
      },
      [CLIENT_TYPE.IN_STORE]: {
        [FinanceType.PURCHASE]: clone(defaultOwnershipRule),
        [FinanceType.LEASE]: clone(defaultOwnershipRule),
        [FinanceType.CASH]: clone(defaultOwnershipRule),
      },
    },
    [Condition.USED]: {
      [CLIENT_TYPE.ONLINE]: {
        [FinanceType.PURCHASE]: clone(defaultOwnershipRule),
        [FinanceType.CASH]: clone(defaultOwnershipRule),
      },
      [CLIENT_TYPE.IN_STORE]: {
        [FinanceType.PURCHASE]: clone(defaultOwnershipRule),
        [FinanceType.CASH]: clone(defaultOwnershipRule),
      },
    },
  };

  const [ownershipOptionsConfig, setOwnershipOptionsConfig] = useState(
    dealer.ownershipOptionsRules || defaultOwnershipOptions,
  );
  const [isDisabledDefaultPayment, disableDefaultPayment] = useState(
    dealer.ownershipOptionsRules.disabledDefaultPayment ||
      defaultOwnershipOptions.disabledDefaultPayment,
  );
  const cleanNullValues = options => {
    Object.keys(options).forEach(condition => {
      Object.keys(options[condition]).forEach(origin => {
        Object.keys(options[condition][origin]).forEach(financeType => {
          const config = cloneDeep(options[condition][origin][financeType]);
          if (config) {
            if (config.maxMiles === '') {
              delete config.maxMiles;
            }
            if (config.maxPrice === '') {
              delete config.maxPrice;
            }
            if (config.maxAge === '') {
              delete config.maxAge;
            }
          }
          options[condition][origin][financeType] = config;
        });
      });
    });
    return options;
  };

  const onSubmit = e => {
    e.preventDefault();
    let ownershipOptionsConfigCopy = cloneDeep(ownershipOptionsConfig);
    ownershipOptionsConfigCopy = cleanNullValues(ownershipOptionsConfigCopy);
    const updateDealerPayload = {
      dealer: {
        id: dealer.id,
        ownershipOptionsRules: ownershipOptionsConfigCopy,
      },
      route: `/dealer/${dealer.id}`,
      // Since the `cleanNullValues` deletes fields, we'll disable diffing as a precaution
      // to avoid any unexpected behavior. This option may be able to be removed with some
      // more testing
      diffDealerBeforeSend: false,
    };
    updateDealerPayload.dealer.ownershipOptionsRules.disabledDefaultPayment = isDisabledDefaultPayment;
    actions.updateDealerRequest(updateDealerPayload);
  };

  const validateRule = (value, min, max) =>
    (value >= min && value <= max) || !value ? null : 'error';

  const changeMax = (property, condition, origin, financeType, value) => {
    const newOwnershipOptionsConfig = cloneDeep(ownershipOptionsConfig);
    newOwnershipOptionsConfig[condition][origin][financeType][property] = value
      ? parseInt(value, 10)
      : '';
    setOwnershipOptionsConfig(newOwnershipOptionsConfig);
  };

  const toggleEnabled = (condition, origin, financeType, value) => {
    const newOwnershipOptionsConfig = cloneDeep(ownershipOptionsConfig);
    set(
      newOwnershipOptionsConfig,
      `${condition}.${origin}.${financeType}.enabled`,
      value,
    );
    setOwnershipOptionsConfig(newOwnershipOptionsConfig);
  };

  const enableByFinanceTypeInputs = (condition, origin) => {
    const enabledPurchase = get(
      ownershipOptionsConfig,
      `${condition}.${origin}.${[FinanceType.PURCHASE]}.enabled`,
    );
    const enabledCash = get(
      ownershipOptionsConfig,
      `${condition}.${origin}.${[FinanceType.CASH]}.enabled`,
    );
    const enabledLease =
      condition === Condition.NEW
        ? get(
            ownershipOptionsConfig,
            `${condition}.${origin}.${[FinanceType.LEASE]}.enabled`,
          )
        : false;

    const validateAtLeastOneFinanceTypeChecked = () =>
      some([enabledPurchase, enabledCash, enabledLease]) ? null : 'error';

    return (
      <FormGroup validationState={validateAtLeastOneFinanceTypeChecked()}>
        <ControlLabel>
          <h3>Finance Types</h3>
        </ControlLabel>
        <span className="form-field-description">
          Enable finance types (at least 1) {origin}
        </span>
        <Checkbox
          id={`${condition}-${origin}-purchase`}
          name={`${condition}-${origin}-purchase`}
          checked={enabledPurchase}
          onChange={e => {
            toggleEnabled(
              condition,
              origin,
              FinanceType.PURCHASE,
              e.target.checked,
            );
          }}
        >
          Purchase
        </Checkbox>
        {condition === Condition.NEW && (
          <Checkbox
            id={`${condition}-${origin}-lease`}
            name={`${condition}-${origin}-lease`}
            checked={enabledLease}
            onChange={e => {
              toggleEnabled(
                condition,
                origin,
                FinanceType.LEASE,
                e.target.checked,
              );
            }}
          >
            Lease
          </Checkbox>
        )}
        <Checkbox
          id={`${condition}-${origin}-cash`}
          name={`${condition}-${origin}-cash`}
          checked={enabledCash}
          onChange={e => {
            toggleEnabled(
              condition,
              origin,
              FinanceType.CASH,
              e.target.checked,
            );
          }}
        >
          Cash
        </Checkbox>
      </FormGroup>
    );
  };

  const excludeConfigInputs = (condition, financeType, origin) => {
    if (
      (condition === Condition.USED || condition === Condition.CPO) &&
      financeType === FinanceType.LEASE
    ) {
      // Finance Type 'Lease' not valid for Used and CPO vehicles
      return false;
    }
    const maxMiles = get(
      ownershipOptionsConfig,
      `${condition}.${origin}.${financeType}.maxMiles`,
    );
    const maxAge = get(
      ownershipOptionsConfig,
      `${condition}.${origin}.${financeType}.maxAge`,
    );
    const maxPrice = get(
      ownershipOptionsConfig,
      `${condition}.${origin}.${financeType}.maxPrice`,
    );
    const enabled = get(
      ownershipOptionsConfig,
      `${condition}.${origin}.${financeType}.enabled`,
    );

    return (
      <>
        <Col xs={12}>
          <h4>{financeType}</h4>
        </Col>
        <Col sm={3} xs={12}>
          <FormGroup validationState={validateRule(maxMiles, 0, 300000)}>
            <ControlLabel>Miles</ControlLabel>
            <FormControl
              type="number"
              id={`${condition}-${origin}-${financeType}-maxMiles`}
              name={`${condition}-${origin}-${financeType}-maxMiles`}
              value={maxMiles}
              min={0}
              max={300000}
              onChange={e =>
                changeMax(
                  'maxMiles',
                  condition,
                  origin,
                  financeType,
                  e.target.value,
                )
              }
              disabled={!enabled}
              placeholder="Maximum Miles"
            />
            {/* <FormControl.Feedback type="error">
              <div className="error">Minimum Value is 0</div>
            </FormControl.Feedback> */}
          </FormGroup>
        </Col>
        <Col sm={3} xs={12}>
          <FormGroup validationState={validateRule(maxAge, 0, 50)}>
            <ControlLabel>Age</ControlLabel>
            <FormControl
              type="number"
              id={`${condition}-${origin}-${financeType}-maxAge`}
              name={`${condition}-${origin}-${financeType}-maxAge`}
              value={maxAge}
              min={0}
              max={50}
              onChange={e =>
                changeMax(
                  'maxAge',
                  condition,
                  origin,
                  financeType,
                  e.target.value,
                )
              }
              placeholder="Maximum Age (Years)"
              disabled={!enabled}
            />
          </FormGroup>
        </Col>
        <Col sm={3} xs={12}>
          <FormGroup validationState={validateRule(maxPrice, 0, 150000)}>
            <ControlLabel>Price</ControlLabel>
            <FormControl
              type="number"
              id={`${condition}-${origin}-${financeType}-maxPrice`}
              name={`${condition}-${origin}-${financeType}-maxPrice`}
              value={maxPrice}
              min={0}
              max={150000}
              onChange={e =>
                changeMax(
                  'maxPrice',
                  condition,
                  origin,
                  financeType,
                  e.target.value,
                )
              }
              placeholder="Maximum Price"
              disabled={!enabled}
            />
          </FormGroup>
        </Col>
      </>
    );
  };

  const excludeConfigInputGroup = (condition, origin) => (
    <>
      <div className="ownership-options-tab-description-container">
        <ControlLabel>
          <h3>Exclude Config</h3>
        </ControlLabel>
        <p>
          This configuration allows you to mark vehicles by age, miles, or price
          to exclude certain ownerships. Any input here is set to the maximum
          Miles, Age, and Price of a vehicle to be considered as an exclusion
          according to the purchase type.
        </p>
        <p>
          A vehicle matching only one of the criteria will be enough to be
          excluded (does not need to match all 3).
        </p>
      </div>
      {Object.keys(FinanceType).map(financeType => (
        <Row key={`exclude-row-${condition}-${origin}-${financeType}`}>
          {excludeConfigInputs(condition, FinanceType[financeType], origin)}
        </Row>
      ))}
    </>
  );

  const hasAllFinanceTypesCheckedProperly = () =>
    Object.keys(ownershipOptionsConfig).every(condition =>
      Object.keys(ownershipOptionsConfig[condition]).every(origin =>
        Object.keys(ownershipOptionsConfig[condition][origin]).some(
          financeType =>
            ownershipOptionsConfig[condition][origin][financeType].enabled,
        ),
      ),
    );

  const handleDefaultPaymentToggle = () => {
    disableDefaultPayment(!isDisabledDefaultPayment);
  };

  return (
    <div className="ownership-options-container">
      <Form
        id="owner-options-form"
        name="owner-options-form"
        onSubmit={onSubmit}
      >
        <div>
          <Checkbox
            id={'pre-select-payment-checked'}
            name={'pre-select-payment-checked'}
            checked={isDisabledDefaultPayment}
            onChange={handleDefaultPaymentToggle}
          >
            Do not pre-select default payment online
          </Checkbox>
        </div>
        <Tabs id="ownership-options-condition-tabs">
          {Object.keys(Condition).map(condition => {
            if (
              condition === Condition.CPO &&
              !dealer?.featureConfig?.useCPO?.enabled
            ) {
              return <div />;
            }
            return (
              <Tab
                eventKey={condition}
                title={Condition[condition]}
                key={`tab-type-${condition}`}
              >
                <div className="ownership-options-tab-content">
                  <Tabs id={`ownership-options-${Condition[condition]}-tabs`}>
                    <Tab
                      eventKey={Origin.INSTORE}
                      title={Origin.INSTORE}
                      className="ownership-options-nested-tab"
                    >
                      <h2>
                        Ownership Options for{' '}
                        <span>{Condition[condition]}</span> Vehicles{' '}
                        <span>{Origin.INSTORE}</span>
                      </h2>
                      {enableByFinanceTypeInputs(
                        Condition[condition],
                        CLIENT_TYPE.IN_STORE,
                      )}
                      {excludeConfigInputGroup(
                        Condition[condition],
                        CLIENT_TYPE.IN_STORE,
                      )}
                    </Tab>
                    <Tab
                      eventKey={Origin.ONLINE}
                      title={Origin.ONLINE}
                      className="ownership-options-nested-tab"
                    >
                      <h2>
                        Ownership Options for{' '}
                        <span>{Condition[condition]}</span> Vehicles{' '}
                        <span>{Origin.ONLINE}</span>
                      </h2>
                      {enableByFinanceTypeInputs(
                        Condition[condition],
                        CLIENT_TYPE.ONLINE,
                      )}
                      {excludeConfigInputGroup(
                        Condition[condition],
                        CLIENT_TYPE.ONLINE,
                      )}
                    </Tab>
                  </Tabs>
                </div>
              </Tab>
            );
          })}
        </Tabs>
        <ButtonGroup>
          <Button disabled={fetching} type="submit" onClick={onBack}>
            {'<< Back'}
          </Button>
          <Button
            bsClass="btn btn-primary btn-large"
            disabled={fetching || !hasAllFinanceTypesCheckedProperly()}
            type="submit"
          >
            Save & Exit{' '}
            {!hasAllFinanceTypesCheckedProperly() && (
              <>
                (Please check at least 1 FinanceType have been checked in all
                tabs)
              </>
            )}
          </Button>
        </ButtonGroup>
      </Form>
    </div>
  );
}

OwnershipOptionsConfig.propTypes = {
  fetching: PropTypes.bool,
  dealer: PropTypes.object,
  actions: PropTypes.object,
  onBack: PropTypes.func,
};

export default OwnershipOptionsConfig;
